import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Snackbar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { authGet, authPost, authPut, AuthRequest, Navigation } from "../../lib";
import { PREOWNED_PREFIX, PREOWNED_SITE_URL } from "../../themes";
import { PreownedImages } from "./PreownedImages";

/** Lookup dropdowns the form needs, loaded in one pass. */
const LOOKUPS = [
  "brand",
  "condition",
  "box",
  "papers",
  "factory_warranty",
  "warranty",
  "status",
  "case_material",
  "dial_color",
  "bezel",
  "strap_material",
  "strap_color",
  "buckle",
  "crystal",
  "case_back",
  "movement",
  "size",
  "complication",
  "accessory_type",
  "accessory_condition",
];

/** Mirrors the CI4 validation rules; accessories use a much shorter set. */
const REQUIRED = {
  watch: [
    "stock_id", "brand_id", "series", "model_no", "condition_id", "box_id",
    "papers_id", "case_material_id", "dial_color_id", "movement_id",
    "size_id", "warranty_id", "status_id",
  ],
  // stock_id is assigned from the new item_id on save.
  accessory: ["brand_id", "model_no", "accessory_type_id", "status_id"],
};

const useStyles = makeStyles((theme) => ({
  // Matches the main admin's add-watch form (AddWatchPage.styles centerMaxWidth).
  page: { maxWidth: 1140, margin: "0 auto", width: "100%" },
  // The accessory form is short enough to sit oddly high without this; the
  // watch form is long and needs no extra offset.
  accessoryOffset: { marginTop: 40 },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
  },
  actions: { display: "flex", alignItems: "center", gap: theme.spacing(1) },
  section: { padding: theme.spacing(2), marginBottom: theme.spacing(2) },
  sectionTitle: {
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
  },
  getInfo: { display: "flex", gap: theme.spacing(1), alignItems: "flex-end" },
  unresolved: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    borderLeft: `3px solid ${theme.palette.warning.main}`,
    backgroundColor: "rgba(255,180,0,0.08)",
  },
  chip: { margin: 2 },
  statLine: {
    display: "flex",
    gap: 14,
    fontSize: 11,
    color: "rgba(0,0,0,0.6)",
    marginTop: 2,
  },
  siteLink: { color: "#1565c0", textDecoration: "none", fontWeight: 500 },
  // Read-only, computed — styled flat so it doesn't look like an input.
  derived: {
    fontSize: 12,
    height: 24,
    lineHeight: "24px",
    padding: "0 5px",
    color: "rgba(0,0,0,0.72)",
  },
  logHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    cursor: "pointer",
    userSelect: "none",
  },
  logCount: { fontSize: 11, color: theme.palette.text.secondary },
  logToggle: {
    marginLeft: "auto",
    fontSize: 11,
    color: "#1565c0",
    fontWeight: 500,
  },
  logTable: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 12,
    marginTop: theme.spacing(1.5),
    "& th": {
      textAlign: "left",
      fontWeight: 700,
      fontSize: 11,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      color: theme.palette.text.secondary,
      borderBottom: "1px solid rgba(0,0,0,0.12)",
      padding: "4px 8px",
    },
    "& td": {
      padding: "3px 8px",
      borderBottom: "1px solid rgba(0,0,0,0.05)",
      verticalAlign: "top",
    },
  },
  logFrom: { color: "rgba(0,0,0,0.45)" },
  // Quill sizes to content by default; give it roughly a dozen lines.
  editor: {
    backgroundColor: "#fff",
    "& .ql-editor": { minHeight: 240, fontSize: 13 },
  },
  error: { color: theme.palette.error.main },
  notice: { color: theme.palette.success.main },
}));

/**
 * Field styling copied from the main admin's add-watch screen (Case.tsx):
 * native controls, 24px tall, 12px text, with the label as a plain <label>
 * above rather than a floating MUI one.
 */
const FIELD = {
  fontSize: 12,
  height: 24,
  padding: "2px 5px",
  border: "1px solid rgba(0, 0, 0, 0.23)",
  borderRadius: 3,
  backgroundColor: "transparent",
  width: "100%",
  boxSizing: "border-box",
};
const SELECT = { ...FIELD, padding: "2px 12px 2px 5px" };
const LABEL = { fontSize: 11, marginBottom: 1, display: "block" };
const ERR_BORDER = "1px solid #d32f2f";
const WARN_BORDER = "1px solid #ed6c02";

function FieldLabel({ children, required }) {
  return (
    <label style={LABEL}>
      {children}
      {required && <span style={{ color: "#d32f2f" }}> *</span>}
    </label>
  );
}

function Hint({ children, color }) {
  return (
    <div style={{ fontSize: 10, color, marginTop: 1, lineHeight: 1.3 }}>
      {children}
    </div>
  );
}

/**
 * Width in twelfths. MUI's `md` takes integers only, so fractional widths
 * (1.5, 4.5) are applied as flexBasis instead — matching how the main admin
 * sizes its fields with explicit percentages.
 */
function gridProps(w) {
  if (Number.isInteger(w)) return { xs: 12, sm: 6, md: w };
  const pct = `${((w / 12) * 100).toFixed(4)}%`;
  return { style: { flexBasis: pct, maxWidth: pct } };
}

/** Forces the next field onto a new row without padding out the current one. */
function Break() {
  return <Grid item xs={12} style={{ height: 0, padding: 0 }} />;
}

/** Section wrapper — keeps the 40-odd fields readable. */
function Section({ title, children }) {
  const classes = useStyles();
  return (
    <Paper className={classes.section}>
      <div className={classes.sectionTitle}>{title}</div>
      <Grid container spacing={2}>
        {children}
      </Grid>
    </Paper>
  );
}

/**
 * Field context. These components MUST live at module scope: defined inside the
 * page they get a new function identity every render, so React remounts the
 * <input> on each keystroke and focus is lost after one character.
 */
/** Bold/italic/underline, lists, links — matches the CI4 TinyMCE toolbar. */
const DESCRIPTION_TEMPLATE =
  "<p><strong>Case:</strong> </p><p><br></p>" +
  "<p><strong>Strap:</strong> </p><p><br></p>" +
  "<p><strong>Movement:</strong> </p><p><br></p>" +
  "<p><strong>Condition:</strong> </p>";

const QUILL_MODULES = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ header: [2, 3, false] }],
    ["link"],
    ["clean"],
  ],
};

const SIZE_SUFFIXES = ["mm", "cm", "in"];

/**
 * log_item.changed comes in three shapes, all of which exist in the 41,939
 * historical rows:
 *   "field => old -> new"   a change with both sides
 *   "field => value"        older CI4 rows that recorded only the new value
 *   "Item Added"            a bare note
 * Multiple changes are joined with " | ".
 */
function parseChanges(changed) {
  return String(changed || "")
    .split(" | ")
    .map((part) => {
      const both = part.match(/^(.+?)\s=>\s(.*?)\s->\s(.*)$/);
      if (both) return { field: both[1], from: both[2], to: both[3] };
      const one = part.match(/^(.+?)\s=>\s(.*)$/);
      if (one) return { field: one[1], from: null, to: one[2] };
      return { note: part };
    })
    .filter((c) => c.note !== "" || c.field);
}

const fmtTime = (t) =>
  !t ? "" : String(t).replace("T", " ").replace(/\..*$/, "").slice(0, 16);

/** Whole dollars everywhere — nothing in this catalog is priced in cents. */
/** Mirrors WatchModel::getUrl() — see PreownedWatchesPage / the generator. */
const encodeModelNo = (v) =>
  String(v ?? "").replace(/\//g, "~").replace(/#/g, "`").replace(/ /g, "_");
const slugPart = (v) =>
  encodeURIComponent(
    encodeModelNo(v).replace(/[^A-Za-z0-9 ]/g, "-").replace(/-+/g, "-"),
  );

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const PRICE_FIELDS = [
  "retail_price",
  "selling_price",
  "special_price",
  "wholesale_price",
];

/**
 * DECIMAL(15,2) comes back as "2000.00". Rounded once when the record lands,
 * rather than formatted at render, so typing into the field is never fighting
 * a formatter mid-keystroke.
 */
function roundPrices(item) {
  const out = { ...item };
  for (const f of PRICE_FIELDS) {
    const v = out[f];
    if (v !== null && v !== undefined && v !== "" && Number.isFinite(Number(v))) {
      out[f] = String(Math.round(Number(v)));
    }
  }
  return out;
}

const FieldCtx = React.createContext({});

/** Dropdown bound to a lookup table; flags anything Get Info couldn't match. */
function Select({ type, field, label, required, xs = 3 }) {
  const { lookups, form, set, missing, unresolved } = useContext(FieldCtx);
  const rows = lookups[type] ?? [];
  const idKey = `${type}_id`;
  const miss = unresolved.find((u) => u.field === field);
  const bad = missing.includes(field);
  return (
    <Grid item {...gridProps(xs)}>
      <FieldLabel required={required}>{label}</FieldLabel>
      <select
        value={form[field] ?? ""}
        onChange={set(field)}
        style={{
          ...SELECT,
          border: bad ? ERR_BORDER : miss ? WARN_BORDER : SELECT.border,
        }}
      >
        <option value="">—</option>
        {rows.map((r) => (
          <option key={r[idKey]} value={r[idKey]}>
            {r[type]}
          </option>
        ))}
      </select>
      {miss && (
        <Hint color="#ed6c02">Catalog: “{miss.pt_value}” — pick closest</Hint>
      )}
    </Grid>
  );
}

function Options({ field, label, options, required, xs = 3 }) {
  const { form, set, missing } = useContext(FieldCtx);
  const bad = missing.includes(field);
  return (
    <Grid item {...gridProps(xs)}>
      <FieldLabel required={required}>{label}</FieldLabel>
      <select
        value={form[field] ?? ""}
        onChange={set(field)}
        style={{ ...SELECT, border: bad ? ERR_BORDER : SELECT.border }}
      >
        <option value="">—</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </Grid>
  );
}

/**
 * `prefix` renders a fixed, greyed character inside the field's border —
 * accessory stock numbers display as "A12345" but are stored bare, exactly as
 * the CI4 view does it (a literal "A" printed before the input).
 */
function Text({ field, label, required, xs = 3, type = "text", prefix }) {
  const { form, set, missing, stockTaken } = useContext(FieldCtx);
  const clash = field === "stock_id" && stockTaken;
  const bad = missing.includes(field) || !!clash;
  const border = bad ? ERR_BORDER : FIELD.border;
  return (
    <Grid item {...gridProps(xs)}>
      <FieldLabel required={required}>{label}</FieldLabel>
      {prefix ? (
        <div
          style={{
            ...FIELD,
            border,
            display: "flex",
            alignItems: "center",
            padding: "0 0 0 5px",
          }}
        >
          <span
            style={{ color: "rgba(0,0,0,0.38)", fontSize: 12, userSelect: "none" }}
          >
            {prefix}
          </span>
          <input
            type={type}
            value={form[field] ?? ""}
            onChange={set(field)}
            style={{
              flex: 1,
              minWidth: 0,
              border: "none",
              outline: "none",
              background: "transparent",
              fontSize: 12,
              padding: "2px 5px 2px 1px",
            }}
          />
        </div>
      ) : (
        <input
          type={type}
          value={form[field] ?? ""}
          onChange={set(field)}
          style={{ ...FIELD, border }}
        />
      )}
      {clash && (
        <Hint color="#d32f2f">
          Used by {stockTaken.kind} {stockTaken.model_no}
        </Hint>
      )}
    </Grid>
  );
}

/**
 * "% Off" — a calculator, not a stored column. Typing a percentage rewrites
 * Price from MSRP; leaving it alone, it shows the discount the two current
 * values already imply.
 *
 * Same arithmetic as the main admin's Pricing row (Retail | % | Our Price):
 * floor, so the derived price never rounds up past the intended discount.
 */
function PercentOff({ xs = 1 }) {
  const { form, set } = useContext(FieldCtx);
  const [typed, setTyped] = useState(null);

  const money = (v) => {
    const n = parseFloat(String(v ?? "").replace(/,/g, ""));
    return Number.isNaN(n) ? 0 : n;
  };
  const retail = money(form.retail_price);
  const price = money(form.selling_price);

  // Derived while idle, literal while being typed — otherwise the field would
  // fight the user, rewriting "1" into "100" as they reach for 15.
  const derived =
    retail > 0 && price > 0 && price < retail
      ? String(Math.round(((retail - price) / retail) * 100))
      : "";
  const shown = typed ?? derived;

  const onChange = (e) => {
    const raw = e.target.value.replace(/[^0-9.]/g, "");
    setTyped(raw);
    const pct = parseFloat(raw);
    if (retail > 0 && !Number.isNaN(pct) && pct >= 0 && pct < 100) {
      set("selling_price")({ target: { value: Math.floor(retail - retail * (pct / 100)) } });
    }
  };

  return (
    <Grid item {...gridProps(xs)}>
      <FieldLabel>% Off</FieldLabel>
      <input
        type="text"
        inputMode="decimal"
        value={shown}
        onChange={onChange}
        onBlur={() => setTyped(null)}
        disabled={!retail}
        title={retail ? "" : "Enter an MSRP first"}
        style={{ ...FIELD, border: FIELD.border, opacity: retail ? 1 : 0.5 }}
      />
    </Grid>
  );
}

function Check({ field, label, xs = 3 }) {
  const { form, set } = useContext(FieldCtx);
  return (
  <Grid item {...gridProps(xs)}>
    <label
      style={{
        fontSize: 12,
        display: "flex",
        alignItems: "center",
        marginTop: 14,
      }}
    >
      <input
        type="checkbox"
        checked={!!form[field]}
        onChange={set(field)}
        style={{ marginRight: 6, accentColor: "#1565c0" }}
      />
      {label}
    </label>
  </Grid>
  );
}

export function PreownedEditWatchPage(props) {
  const classes = useStyles();
  const itemId = props?.pageRoute?.params?.id;
  const isEdit = !!itemId;
  const routeKind = /accessor/i.test(
    props?.pageRoute?.location?.pathname || window.location.pathname,
  )
    ? "accessory"
    : "watch";
  const [kind, setKind] = useState(routeKind);
  const isAccessory = kind === "accessory";

  const [form, setForm] = useState({ status_id: 1 });
  const [complications, setComplications] = useState([]);
  const [gtin, setGtin] = useState("");
  const [lookups, setLookups] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [missing, setMissing] = useState([]);
  const [ptModelNo, setPtModelNo] = useState("");
  const [unresolved, setUnresolved] = useState([]);
  const [pulling, setPulling] = useState(false);
  // Quill rewrites TinyMCE-era markup (<span style="font-weight:bold"> -> <strong>,
  // <br> runs -> <p>) just by loading it. Only send the description when the
  // user has genuinely edited it, so opening a watch can't silently rewrite it.
  const [descTouched, setDescTouched] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logOpen, setLogOpen] = useState(true);
  const [descOpen, setDescOpen] = useState(false);
  // stock_id is UNIQUE; checked as you type so a collision surfaces here
  // rather than as a driver error on save.
  const [stockTaken, setStockTaken] = useState(null);
  const [stats, setStats] = useState(null);
  // The stock number the record loaded with — a duplicate keeping it would
  // always collide, since the copy has no exclusion.
  const [originalStockId, setOriginalStockId] = useState("");
  // Images chosen while adding, before an item_id exists to attach them to.
  const [pendingImages, setPendingImages] = useState([]);

  const set = useCallback(
    (field) => (e) => {
      const v =
        e?.target?.type === "checkbox" ? (e.target.checked ? 1 : 0) : e.target.value;
      setForm((p) => ({ ...p, [field]: v }));
      setMissing((m) => m.filter((x) => x !== field));
    },
    [],
  );

  useEffect(() => {
    (async () => {
      const results = await Promise.all(
        LOOKUPS.map((t) => authGet(`/preowned/attributes/${t}`)),
      );
      const map = {};
      LOOKUPS.forEach((t, i) => {
        map[t] = results[i]?.data?.rows ?? [];
      });
      setLookups(map);
      if (!isEdit) {
        if (routeKind === "watch") {
          setForm((prev) =>
            prev.description ? prev : { ...prev, description: DESCRIPTION_TEMPLATE },
          );
        }
        setLoading(false);
      }
    })();
  }, [isEdit, routeKind]);

  useEffect(() => {
    const q = new URLSearchParams(
      props?.pageRoute?.location?.search || window.location.search,
    );
    if (q.get("added")) setNotice("Watch added.");
  }, [props]);

  // Clear any confirmation on its own after a few seconds.
  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(""), 3500);
    return () => clearTimeout(t);
  }, [notice]);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      setLoading(true);
      const { data, error: err } = await authGet(`/preowned/watches/${itemId}`);
      if (err) setError(err.message || "Failed to load");
      else {
        setForm(roundPrices(data?.item ?? {}));
        setOriginalStockId(String(data?.item?.stock_id ?? ""));
        setComplications(data?.complications ?? []);
        setGtin(data?.gtin ?? "");
        setDescTouched(false);
        // Trust the record over the URL.
        setKind(data?.item?.accessory_type_id != null ? "accessory" : "watch");
        setLogs(data?.logs ?? []);
        setStats(data?.stats ?? null);
      }
      setLoading(false);
    })();
  }, [itemId, isEdit]);

  /** Prefill from the main catalog. Unmatched labels come back explicitly. */
  async function getInfo() {
    const term = (ptModelNo || form.model_no || "").trim();
    if (!term) return;
    setPulling(true);
    setError("");
    setUnresolved([]);
    const { data, error: err } = await authGet(
      `/preowned/pt-lookup?model_no=${encodeURIComponent(term)}`,
    );
    setPulling(false);
    if (err) return setError(err.message || "Lookup failed");
    if (!data?.found) return setError(`No main-catalog watch matches "${term}".`);

    setForm((p) => roundPrices({ ...p, ...data.fields }));
    setUnresolved(data.unresolved ?? []);
    if (data.gtin) setGtin(data.gtin);
    setNotice(
      `Pulled from the main catalog${
        data.unresolved?.length ? ` — ${data.unresolved.length} field(s) need a manual pick` : ""
      }.`,
    );
  }

  /**
   * Clears the stock number first, then saves as new — so the user lands on an
   * empty required field instead of an error about the one they copied.
   */
  function duplicate() {
    if (!isAccessory && String(form.stock_id ?? "") === originalStockId) {
      setForm((p) => ({ ...p, stock_id: "" }));
      setMissing(["stock_id"]);
      setError("Enter the Stock ID for the new watch, then press Duplicate again.");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    save(true);
  }

  /**
   * `asNew` reproduces the CI4 Duplicate button: repost the current form to the
   * create endpoint instead of the update one. Images and complications aren't
   * carried over — a duplicate is a new record that borrows the spec.
   */
  async function save(asNew = false) {
    if (stockTaken) {
      setError(
        `Stock ID ${form.stock_id} is already used by ${stockTaken.kind} ${stockTaken.model_no}.`,
      );
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    /**
     * Duplicating clears the stock number rather than generating one. Watch
     * stock ids are externally assigned — they aren't monotonic with item_id
     * (70517 predates 70092) and the range runs to 11 digits, so MAX+1 would
     * invent a number that matches no physical tag. Accessories differ: theirs
     * IS the item_id, so the backend fills it in.
     */
    if (asNew && !isAccessory && String(form.stock_id ?? "").trim() === "") {
      setError("Enter the Stock ID for the new watch.");
      setMissing(["stock_id"]);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const gaps = REQUIRED[kind].filter((f) => form[f] == null || form[f] === "");
    setMissing(gaps);
    if (gaps.length) {
      setError(`Missing required field(s): ${gaps.join(", ")}`);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setSaving(true);
    setError("");
    const body = { ...form, complication_id: complications, gtin };
    if (isEdit && !descTouched) delete body.description;
    if (asNew) {
      delete body.item_id;
      if (isAccessory) delete body.stock_id;
    }
    const { data, error: err } = isEdit && !asNew
      ? await authPut(`/preowned/watches/${itemId}`, body)
      : await authPost("/preowned/watches", body);
    setSaving(false);
    if (err) return setError(err.message || "Save failed");
    if ((!isEdit || asNew) && data?.item_id) {
      // Images picked before the record existed. Uploaded here rather than
      // failing silently; a failure is surfaced instead of losing the files,
      // and the watch itself is already saved either way.
      if (pendingImages.length) {
        setSaving(true);
        try {
          for (const p of pendingImages) {
            const fd = new FormData();
            // Text fields before files — multer parses in stream order.
            if (p.watermark) fd.append("watermark_color", p.watermark);
            fd.append("afile", p.file);
            await AuthRequest.post(`/preowned/images/${data.item_id}`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          }
        } catch (e) {
          setSaving(false);
          setError(
            "Watch saved, but an image failed to upload: " +
              (e?.response?.data?.message || e.message) +
              " — add it from the edit screen.",
          );
          return;
        }
        setSaving(false);
      }
      // The redirect remounts this page, so the confirmation rides along in
      // the URL rather than in state.
      Navigation.go(`${PREOWNED_PREFIX}/edit-watch/${data.item_id}?added=1`);
      return;
    }
    setNotice(isAccessory ? "Accessory updated." : "Watch updated.");
  }

  // Debounced availability check on the stock number.
  useEffect(() => {
    const id = String(form.stock_id ?? "").trim();
    if (!id) return setStockTaken(null) || undefined;
    const t = setTimeout(async () => {
      const qs = new URLSearchParams({ stock_id: id });
      if (itemId) qs.set("exclude_item_id", itemId);
      const { data } = await authGet(`/preowned/stock-id-check?${qs}`);
      setStockTaken(data && !data.available ? data.taken_by : null);
    }, 350);
    return () => clearTimeout(t);
  }, [form.stock_id, itemId]);

  // The storefront slug needs the brand NAME; the form only holds brand_id.
  /**
   * Derived from the expiry date alone. Previously split across a Yes/No
   * lookup and a date, which could disagree — 512 watches are flagged "Time
   * remaining" with a date already past.
   */
  const factoryWarranty = useMemo(() => {
    const d = String(form.factory_warranty_date ?? "").slice(0, 10);
    if (!d) return "No";
    // Parsed as local time, not UTC — "2027-03-01" through Date() alone is
    // UTC midnight, which lands in February for anyone west of Greenwich and
    // would show the wrong month.
    const exp = new Date(`${d}T23:59:59`);
    if (Number.isNaN(exp.getTime())) return "No";
    if (exp < new Date()) return "No — expired";
    // Month and year only, matching what the storefront prints. Split from the
    // string rather than formatted from the Date for the same reason.
    const [y, m] = d.split("-");
    return `Yes — expires ${MONTHS[Number(m) - 1] ?? m} ${y}`;
  }, [form.factory_warranty_date]);

  const brandLabel = useMemo(
    () =>
      (lookups.brand ?? []).find((b) => b.brand_id === Number(form.brand_id))
        ?.brand ?? "",
    [lookups.brand, form.brand_id],
  );

  const fieldCtx = useMemo(
    () => ({ lookups, form, set, missing, unresolved, stockTaken }),
    [lookups, form, set, missing, unresolved, stockTaken],
  );

  if (loading) {
    return (
      <Box p={6} display="flex" justifyContent="center">
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  return (
    <FieldCtx.Provider value={fieldCtx}>
    <Box
      p={3}
      className={`${classes.page} ${isAccessory ? classes.accessoryOffset : ""}`}
    >
      <div className={classes.header}>
        <div>
          <Typography variant="h5">
            {isEdit
              ? `Edit ${isAccessory ? "Accessory" : "Pre-Owned"} — ${form.stock_id ?? itemId}`
              : isAccessory
              ? "Add Accessory"
              : "Add Pre-Owned Watch"}
          </Typography>
          {isEdit && (
            <div className={classes.statLine}>
              {stats && (
                <>
                  <span>
                    Viewed <strong>{stats.viewed.toLocaleString()}</strong>
                  </span>
                  <span>
                    Added to cart{" "}
                    <strong>{stats.added_to_cart.toLocaleString()}</strong>
                  </span>
                </>
              )}
              {PREOWNED_SITE_URL && form.stock_id && brandLabel && (
                <a
                  className={classes.siteLink}
                  href={`${PREOWNED_SITE_URL}/${slugPart(brandLabel)}-${slugPart(
                    form.series ?? "",
                  )}-${encodeModelNo(form.model_no)}-${form.stock_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View on site
                </a>
              )}
            </div>
          )}
        </div>
        <div className={classes.actions}>
          {notice && (
            <Typography variant="body2" className={classes.notice}>
              {notice}
            </Typography>
          )}
          <Button
            onClick={() => Navigation.go(`${PREOWNED_PREFIX}/watches`)}
            disabled={saving}
          >
            Back to grid
          </Button>
          {isEdit && (
            <Button
              variant="outlined"
              onClick={duplicate}
              disabled={saving || !!stockTaken}
              title="Save these details as a new record"
            >
              Duplicate
            </Button>
          )}
          <Button
            variant="contained"
            color="secondary"
            onClick={() => save(false)}
            disabled={saving}
          >
            {saving ? "Saving…" : isEdit ? "Save Changes" : isAccessory ? "Add Accessory" : "Add Watch"}
          </Button>
        </div>
      </div>

      {error && (
        <Typography className={classes.error} gutterBottom>
          {error}
        </Typography>
      )}

      {/* Watches only. The main catalog holds no accessories, so a lookup
          here can never match (0 of 400 accessory model numbers do), and a
          stray match would write watch attributes onto an accessory row. */}
      {!isAccessory && (
      <Paper className={classes.section}>
        {/* The most-used feature in the CI4 admin: pull specs by model number
            rather than filling forty fields by hand. */}
        <div className={classes.sectionTitle}>Pull From Main Catalog</div>
        <div className={classes.getInfo}>
          <div style={{ width: 300 }}>
            <FieldLabel>Main catalog model no.</FieldLabel>
            <input
              value={ptModelNo}
              onChange={(e) => setPtModelNo(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && getInfo()}
              style={FIELD}
            />
          </div>
          <Button
            variant="outlined"
            size="small"
            startIcon={<SearchIcon style={{ fontSize: 14 }} />}
            onClick={getInfo}
            disabled={pulling}
            // Matches the 24px native input beside it.
            style={{ height: 24, fontSize: 12, padding: "0 10px", minWidth: 0 }}
          >
            {pulling ? "Looking up…" : "Get Info"}
          </Button>
        </div>
        {!!unresolved.length && (
          <div className={classes.unresolved}>
            <Typography variant="body2">
              These had no pre-owned equivalent and were left for you to set:{" "}
              {unresolved.map((u) => (
                <Chip
                  key={u.field}
                  size="small"
                  className={classes.chip}
                  label={`${u.type}: ${u.pt_value}`}
                />
              ))}
            </Typography>
          </div>
        )}
      </Paper>
      )}

      {isAccessory ? (
        <>
      {/* One row: 1.75 + 2 + 1.75 + 2 + 1.25 + 0.75 x 3 + 1 = 12 */}
      <Section title="Accessory">
        <Text field="stock_id" label="Stock ID" xs={1.75} prefix="A" />
        <Select type="brand" field="brand_id" label="Brand" required xs={2} />
        <Select
          type="accessory_type"
          field="accessory_type_id"
          label="Accessory Type"
          required
          xs={1.75}
        />
        <Text field="model_no" label="Model Number" required xs={2} />
        <Select
          type="accessory_condition"
          field="accessory_condition_id"
          label="Condition"
          xs={1.25}
        />

        <Text field="case_width" label="Width" xs={0.75} />
        <Text field="case_length" label="Length" xs={0.75} />
        <Text field="case_thickness" label="Thickness" xs={0.75} />
        <Options
          field="size_suffix"
          label="Units"
          options={SIZE_SUFFIXES}
          xs={1}
        />
      </Section>

      {/* 1.5 x 5 = 7.5 */}
      <Section title="Pricing & Status">
        <Text field="selling_price" label="Price" xs={1.5} />
        <Text field="special_price" label="Sp. Price" xs={1.5} />
        <Check field="on_special" label="On Special" xs={1.5} />
        <Select type="status" field="status_id" label="Status" required xs={1.5} />
        <Text field="posted_date" label="Available Date" type="date" xs={1.5} />
      </Section>

        </>
      ) : (
        <>
      {/* One row of twelve: 1 + 1.5 + 5 + 1.5 + 1.5 + 1.5 */}
      <Section title="Brand & Model">
        <Text field="stock_id" label="Stock ID" required xs={1} />
        <Select type="brand" field="brand_id" label="Brand" required xs={1.5} />
        <Text field="series" label="Series" required xs={5} />
        <Text field="model_no" label="Model Number" required xs={1.5} />
        <Text field="pt_model_no" label="PT Model No" xs={1.5} />
        <Check field="limited_edition" label="Limited Edition" xs={1.5} />
      </Section>

      <Section title="Pricing & Status">
        {/* 12 of 12. Multiple Units and the WF/WS toggles live on the grid. */}
        <Text field="retail_price" label="MSRP" xs={1} />
        <Check field="display_msrp" label="Display MSRP" xs={1.5} />
        <PercentOff xs={1} />
        <Text field="selling_price" label="Price" xs={1} />
        <Check field="on_special" label="On Special" xs={1.5} />
        <Text field="special_price" label="Sp. Price" xs={1} />
        <Select type="status" field="status_id" label="Status" required xs={2} />
        <Text field="posted_date" label="Available Date" type="date" xs={1.5} />
        <Text field="date_sold" label="Date Sold" type="date" xs={1.5} />
      </Section>

      <Section title="Condition & Papers">
        {/* Rows: 1.5+1.5+3.75+1.5+3.75 = 12, then 2+2.5+2+3+1+1.5 = 12 */}
        <Select type="condition" field="condition_id" label="Condition" required xs={1.5} />
        <Select type="box" field="box_id" label="Box" required xs={1.5} />
        <Text field="box_comments" label="Box Notes" xs={3.75} />
        <Select type="papers" field="papers_id" label="Papers" required xs={1.5} />
        <Text field="papers_comments" label="Papers Notes" xs={3.75} />

        <Break />
        {/* The separate "Factory Warranty" Yes/No select is retired — the
            expiry date now answers it on its own. Kept commented rather than
            deleted; the column is still written by the CI4 admin.
        <Select
          type="factory_warranty"
          field="factory_warranty_id"
          label="Factory Warranty"
          xs={3}
        /> */}
        <Text
          field="factory_warranty_date"
          label="Factory Warranty Exp Date"
          type="date"
          xs={2}
        />
        <Grid item {...gridProps(2.5)}>
          <FieldLabel>Factory Warranty (Frontend)</FieldLabel>
          <div className={classes.derived}>{factoryWarranty}</div>
        </Grid>
        <Check field="service_warranty" label="Service Warranty" xs={2} />
        <Select type="warranty" field="warranty_id" label="Warranty" required xs={3} />
        <Text field="year" label="Year" xs={1} />
        <Check field="show_circa" label="Circa" xs={1.5} />
      </Section>

      <Section title="Specifications">
        {/* Row 1: 4 x 3 = 12 */}
        <Select type="case_material" field="case_material_id" label="Case Material" required />
        <Select type="dial_color" field="dial_color_id" label="Dial Color" required />
        <Select type="bezel" field="bezel_id" label="Bezel" />
        <Select type="crystal" field="crystal_id" label="Crystal" />

        {/* Row 2: 8 x 1.5 = 12 */}
        <Select type="case_back" field="case_back_id" label="Case Back" xs={1.5} />
        <Select type="size" field="size_id" label="Size" required xs={1.5} />
        <Text field="case_width" label="Case Width (mm)" xs={1.5} />
        <Text field="case_length" label="Case Length (mm)" xs={1.5} />
        <Text field="case_thickness" label="Thickness (mm)" xs={1.5} />
        <Text field="lug_width" label="Lug Width (mm)" xs={1.5} />
        <Text field="water_resistance" label="Water Resistance" xs={1.5} />
        <Check field="screw_down_crown" label="SD Crown" xs={1.5} />

        {/* Row 3: 2.25 + 1.5 + 2.25 + 3 + 1.5 + 1.5 = 12 */}
        <Select type="strap_material" field="strap_material_id" label="Strap Material" xs={2.25} />
        <Select type="strap_color" field="strap_color_id" label="Strap Color" xs={1.5} />
        <Select type="buckle" field="buckle_id" label="Buckle" xs={2.25} />
        <Text field="strap_notes" label="Strap Notes" xs={3} />
        <Select type="movement" field="movement_id" label="Movement" required xs={1.5} />
        <Check field="cosc" label="COSC" xs={1.5} />
      </Section>

        </>
      )}

      {!isAccessory && (
      <Section title="Complications">
        <Grid item xs={12}>
          <Box display="flex" flexWrap="wrap">
            {(lookups.complication ?? []).map((c) => (
              <label
                key={c.complication_id}
                style={{
                  width: 190,
                  fontSize: 12,
                  display: "flex",
                  alignItems: "center",
                  marginBottom: 4,
                }}
              >
                <input
                  type="checkbox"
                  checked={complications.includes(c.complication_id)}
                  onChange={(e) =>
                    setComplications((prev) =>
                      e.target.checked
                        ? [...prev, c.complication_id]
                        : prev.filter((x) => x !== c.complication_id),
                    )
                  }
                  style={{ marginRight: 6, accentColor: "#1565c0" }}
                />
                {c.complication}
              </label>
            ))}
          </Box>
        </Grid>
      </Section>
      )}

      {/* Collapsed by default — the editor is 240px tall and pushes the images
          and activity log below the fold on every load. */}
      <Paper className={classes.section}>
        <div
          className={classes.logHeader}
          onClick={() => setDescOpen((o) => !o)}
        >
          <span className={classes.sectionTitle} style={{ marginBottom: 0 }}>
            Description
          </span>
          <span className={classes.logCount}>
            {form.description ? "" : "empty"}
          </span>
          <span className={classes.logToggle}>{descOpen ? "Hide" : "Show"}</span>
        </div>
        <Grid container spacing={2} style={{ display: descOpen ? undefined : "none" }}>
          <Grid item xs={12}>
          <ReactQuill
            theme="snow"
            className={classes.editor}
            modules={QUILL_MODULES}
            value={form.description ?? ""}
            onChange={(html) => {
              setDescTouched(true);
              setForm((prev) => ({ ...prev, description: html }));
            }}
          />
            {isEdit && !descTouched && (
              <Hint color="rgba(0,0,0,0.5)">
                Saved as-is unless you edit it — the editor reformats older HTML.
              </Hint>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Shown while adding too: PreownedImages holds the files and the save
          handler uploads them once the record has an id. */}
      <Section title="Images">
        <Grid item xs={12}>
          <PreownedImages
            itemId={isEdit ? Number(itemId) : null}
            imageDir={form.image_dir}
            onPending={setPendingImages}
          />
        </Grid>
      </Section>

      {isEdit && (
        <Paper className={classes.section}>
          <div
            className={classes.logHeader}
            onClick={() => setLogOpen((o) => !o)}
          >
            <span className={classes.sectionTitle} style={{ marginBottom: 0 }}>
              Activity Log
            </span>
            <span className={classes.logCount}>
              {logs.length} {logs.length === 1 ? "entry" : "entries"}
            </span>
            <span className={classes.logToggle}>{logOpen ? "Hide" : "Show"}</span>
          </div>

          {logOpen &&
            (logs.length ? (
              <table className={classes.logTable}>
                <thead>
                  <tr>
                    <th style={{ width: 130 }}>Changed</th>
                    <th style={{ width: 130 }}>User</th>
                    <th style={{ width: 170 }}>Field</th>
                    <th>From</th>
                    <th>To</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((l) =>
                    parseChanges(l.changed).map((c, i) => (
                      <tr key={`${l.log_id}-${i}`}>
                        <td>{i === 0 ? fmtTime(l.log_time) : ""}</td>
                        <td>
                          {i === 0 ? l.user || `user #${l.user_id}` : ""}
                        </td>
                        {c.note ? (
                          <td colSpan={3}>
                            <em>{c.note}</em>
                          </td>
                        ) : (
                          <>
                            <td>
                              <strong>{c.field}</strong>
                            </td>
                            <td className={classes.logFrom}>
                              {c.from === null ? <em>n/a</em> : c.from || "—"}
                            </td>
                            <td>{c.to || "—"}</td>
                          </>
                        )}
                      </tr>
                    )),
                  )}
                </tbody>
              </table>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No activity recorded.
              </Typography>
            ))}
        </Paper>
      )}

      <Divider style={{ margin: "16px 0" }} />
      <Box display="flex" justifyContent="flex-end" gridGap={8} pb={4}>
        <Button onClick={() => Navigation.go(`${PREOWNED_PREFIX}/watches`)}>
          Back to grid
        </Button>
        {isEdit && (
          <Button
            variant="outlined"
            onClick={duplicate}
            disabled={saving || !!stockTaken}
            title="Save these details as a new record"
          >
            Duplicate
          </Button>
        )}
        <Button
          variant="contained"
          color="secondary"
          onClick={() => save(false)}
          disabled={saving}
        >
          {saving ? "Saving…" : isEdit ? "Save Changes" : isAccessory ? "Add Accessory" : "Add Watch"}
        </Button>
      </Box>
    </Box>

      {/* The actions bar is at the top of a long form, so the inline notice
          is easy to miss after scrolling. */}
      <Snackbar
        open={!!notice}
        autoHideDuration={3500}
        onClose={() => setNotice("")}
        message={notice}
      />
    </FieldCtx.Provider>
  );
}
export default PreownedEditWatchPage;
